import {
	type Component,
	defineComponent,
	h,
	type PropType,
} from 'vue';
import TypographyRoot from './index.vue';
import type { TypographyVariant } from './tokens';

/**
 * Creates a typed convenience component for a typography recipe.
 *
 * @param name Vue component name used in devtools and diagnostics.
 * @param variant Typography recipe applied by {@link TypographyRoot}.
 * @param defaultAs Semantic element rendered when callers do not override `as`.
 * @returns A render-only wrapper around {@link TypographyRoot}.
 */
function createTypographyVariantComponent(
	name: string,
	variant: TypographyVariant,
	defaultAs?: keyof HTMLElementTagNameMap,
): Component {
	return defineComponent({
		name,
		inheritAttrs: false,
		props: {
			as: {
				default: defaultAs,
				type: String as PropType<keyof HTMLElementTagNameMap>,
			},
		},
		setup(props, { attrs, slots }) {
			return () => h(
				TypographyRoot,
				{
					...attrs,
					as: props.as,
					variant,
				},
				slots,
			);
		},
	});
}

const TypographyBody1 = createTypographyVariantComponent(
	'TypographyBody1',
	'body1',
	'p',
);
const TypographyBody1Strong = createTypographyVariantComponent(
	'TypographyBody1Strong',
	'body1Strong',
	'p',
);
const TypographyBody2 = createTypographyVariantComponent(
	'TypographyBody2',
	'body2',
	'p',
);
const TypographyBody2Emphasis = createTypographyVariantComponent(
	'TypographyBody2Emphasis',
	'body2Emphasis',
	'p',
);
const TypographyButton = createTypographyVariantComponent(
	'TypographyButton',
	'button',
	'span',
);
const TypographyCaption1 = createTypographyVariantComponent(
	'TypographyCaption1',
	'caption1',
	'span',
);
const TypographyCaption2 = createTypographyVariantComponent(
	'TypographyCaption2',
	'caption2',
	'span',
);
const TypographyH1 = createTypographyVariantComponent(
	'TypographyH1',
	'h1',
	'h1',
);
const TypographyH2 = createTypographyVariantComponent(
	'TypographyH2',
	'h2',
	'h2',
);
const TypographyH3 = createTypographyVariantComponent(
	'TypographyH3',
	'h3',
	'h3',
);
const TypographyH4 = createTypographyVariantComponent(
	'TypographyH4',
	'h4',
	'h4',
);
const TypographyH5 = createTypographyVariantComponent(
	'TypographyH5',
	'h5',
	'h5',
);
const TypographyH6 = createTypographyVariantComponent(
	'TypographyH6',
	'h6',
	'h6',
);
const TypographyLink = createTypographyVariantComponent(
	'TypographyLink',
	'link',
	'a',
);
const TypographySubtitle = createTypographyVariantComponent(
	'TypographySubtitle',
	'subtitle',
	'p',
);
const TypographySubtitleStrong = createTypographyVariantComponent(
	'TypographySubtitleStrong',
	'subtitleStrong',
	'p',
);

const TypographyComponent = Object.assign(
	TypographyRoot,
	{
		Body1: TypographyBody1,
		Body1Strong: TypographyBody1Strong,
		Body2: TypographyBody2,
		Body2Emphasis: TypographyBody2Emphasis,
		Button: TypographyButton,
		Caption1: TypographyCaption1,
		Caption2: TypographyCaption2,
		H1: TypographyH1,
		H2: TypographyH2,
		H3: TypographyH3,
		H4: TypographyH4,
		H5: TypographyH5,
		H6: TypographyH6,
		Link: TypographyLink,
		Subtitle: TypographySubtitle,
		SubtitleStrong: TypographySubtitleStrong,
	},
) as typeof TypographyRoot & {
	Body1: typeof TypographyBody1;
	Body1Strong: typeof TypographyBody1Strong;
	Body2: typeof TypographyBody2;
	Body2Emphasis: typeof TypographyBody2Emphasis;
	Button: typeof TypographyButton;
	Caption1: typeof TypographyCaption1;
	Caption2: typeof TypographyCaption2;
	H1: typeof TypographyH1;
	H2: typeof TypographyH2;
	H3: typeof TypographyH3;
	H4: typeof TypographyH4;
	H5: typeof TypographyH5;
	H6: typeof TypographyH6;
	Link: typeof TypographyLink;
	Subtitle: typeof TypographySubtitle;
	SubtitleStrong: typeof TypographySubtitleStrong;
};

export {
	createTypographyVariantComponent,
	TypographyBody1,
	TypographyBody1Strong,
	TypographyBody2,
	TypographyBody2Emphasis,
	TypographyButton,
	TypographyCaption1,
	TypographyCaption2,
	TypographyH1,
	TypographyH2,
	TypographyH3,
	TypographyH4,
	TypographyH5,
	TypographyH6,
	TypographyLink,
	TypographyRoot,
	TypographySubtitle,
	TypographySubtitleStrong,
};
export type { TypographyVariant } from './tokens';
export default TypographyComponent;
